import { Button, Flex, Text } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import UTMLink from "@components/UTMLink";
import { UNSTAKE_GAIN_NUDGE } from "@constants/analytics";
import { useVaultTvlApy } from "@hooks/useVaultTvlApy";
import React, { Fragment } from "react";
import { trackEvent } from "utils";

type TGainOpportunitiesCard = {
  id: string;
  title: string;
  logo: string;
  apy: string;
  tvl: string;
  link: string;
  divider?: boolean;
};

type TGainOpportunities = {
  closeModal: () => void;
  action: () => void;
  hasActionButton?: boolean;
  eventName?: string;
};

const GainOpportunities = ({
  closeModal,
  action,
  hasActionButton = true,
  eventName = UNSTAKE_GAIN_NUDGE,
}: TGainOpportunities) => {
  const { formattedApy = "-", formattedTvl = "-" } = useVaultTvlApy("agETH");
  const { formattedApy: yieldApy = "-", formattedTvl: yieldTvl = "-" } =
    useVaultTvlApy("hgETH");
  return (
    <Fragment>
      <Flex direction={"column"} gap="8px">
        <Text
          fontSize={{ base: "12px", md: "14px" }}
          lineHeight={{ base: "20px", md: "20px" }}
          fontWeight={400}
        >
          Don’t miss the chance to earn ~20% APR or 7+ Airdrops with rsETH
        </Text>
        <Flex bgColor={"#EBF9F9"} borderRadius={"12px"} direction={"column"}>
          <GainOpportunitiesCard
            id="airdrop"
            title="Airdrop Gain"
            logo="/assets/agETH.png"
            link="/gain/airdrop-gain/"
            apy={formattedApy}
            tvl={formattedTvl}
            divider
          />
          <GainOpportunitiesCard
            id="yield"
            title="High Gain"
            logo="/assets/vaults/hgeth.svg"
            link="/gain/growth-vault/"
            apy={yieldApy}
            tvl={yieldTvl}
          />
        </Flex>
      </Flex>
      {hasActionButton && (
        <Flex
          width={"100%"}
          justifyContent={"space-between"}
          direction={{ base: "column", md: "row" }}
          gap={{ base: "12px", md: 0 }}
        >
          <Button
            width={{ base: "100%", md: "48%" }}
            padding={"24px 45px"}
            backgroundColor="white"
            color="#075A5A"
            _hover={{
              backgroundColor: "white",
              color: "#075A5A",
            }}
            alignSelf="center"
            borderRadius="0.5rem"
            border={"1px solid #EEEEEF"}
            cursor={"pointer"}
            onClick={() => {
              closeModal();
              action();
              trackEvent(eventName, {
                cta_location: "UnstakePopup",
                cta: "Skip Rewards & Unstake",
              });
            }}
          >
            Skip Rewards & Unstake
          </Button>
          <UTMLink
            href="/gain"
            width={{ base: "100%", md: "48%" }}
            isExternal={true}
          >
            <Button
              padding={"24px 45px"}
              width="100%"
              backgroundColor="#075A5A"
              color="white"
              _hover={{
                backgroundColor: "#075A5A",
                color: "white",
              }}
              alignSelf="center"
              borderRadius="0.5rem"
              cursor={"pointer"}
              onClick={() => {
                trackEvent(eventName, {
                  cta_location: "UnstakePopup",
                  cta: "Earn Top Rewards",
                });
              }}
            >
              Earn Top Rewards
            </Button>
          </UTMLink>
        </Flex>
      )}
    </Fragment>
  );
};

const GainOpportunitiesCard = ({
  id,
  title,
  logo,
  apy,
  tvl,
  link,
  divider = false,
}: TGainOpportunitiesCard) => {
  const extraProps = divider ? { borderBottom: "1px solid #B6DFDF" } : {};
  return (
    <UTMLink href={link} key={id} isExternal={true}>
      <Flex
        padding={{ base: "16px", md: "24px" }}
        justifyContent={"space-between"}
        width={"100%"}
        gap="8px"
        cursor={"pointer"}
        {...extraProps}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          justifyContent={"space-between"}
          gap="10px"
          flex={1}
        >
          <Flex alignItems={"center"} gap="6px">
            <BaseImage src={logo} width={20} height={20} alt={""} />
            <Text
              fontSize={{ base: "12px", md: "14px" }}
              lineHeight={{ base: "20px", md: "20px" }}
              fontWeight={400}
            >
              {title}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            gap="12px"
            pl={{ base: "26px", md: 0 }}
          >
            <Flex>
              <Stats label={"Estimated APR"} value={apy} />
              <Stats label={"TVL"} value={tvl} />
            </Flex>
          </Flex>
        </Flex>

        <BaseImage src={"/assets/arrow.svg"} width={24} height={24} alt={""} />
      </Flex>
    </UTMLink>
  );
};

export default GainOpportunities;

const Stats = ({ label, value }: { label: string; value: string }) => {
  return (
    <Flex gap="3px">
      <Text
        fontSize={{ base: "12px", md: "14px" }}
        lineHeight={{ base: "20px", md: "20px" }}
        fontWeight={400}
      >
        {label}:
      </Text>
      <Text
        minWidth={"60px"}
        fontSize={{ base: "12px", md: "14px" }}
        lineHeight={{ base: "20px", md: "20px" }}
        fontWeight={500}
      >
        {value}
      </Text>
    </Flex>
  );
};
